import React, { useEffect, useState, useContext } from 'react'
import classNames from 'classnames'
import { resolvePrefix } from '@toasttab/shared/src/utils/assetUtils'
import { localizeLink } from '@toasttab/shared/src/utils/linkUtils'
import { I18nextContext } from '@toasttab/shared/src/context/i18nextContext'
import { useI18next } from '@toasttab/shared/src/hooks/useI18next'
import NavRegionSwitcher from '@toasttab/shared/src/components/organisms/Navigation/Contentful/RegionMenu/NavRegionSwitcher'
import SalesCta from '@toasttab/shared/src/components/organisms/Navigation/Contentful/SalesCta'
import { NavContextProvider } from '@toasttab/shared/src/components/organisms/Navigation/Contentful/useNavContext'

interface SimpleStaticNavProps {
  loginUrl?: string
  showSalesCta?: boolean
  salesPhoneNumber?: string
  showRegionSwitcher?: boolean
  localizedEntries?: any[]
  siteId?: number
  siteRegion?: string
}

const SimpleStaticNav: React.FC<SimpleStaticNavProps> = ({
  loginUrl = 'https://pos.toasttab.com',
  salesPhoneNumber,
  showRegionSwitcher = false,
  localizedEntries,
  siteId = 1,
  siteRegion = 'us'
}) => {
  const [isKeyboardUser, setIsKeyboardUser] = useState(false)
  const localizationContext = useContext(I18nextContext)
  const { t } = useI18next()

  // Detect keyboard navigation for proper focus indicators
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Detect Tab key usage
      if (event.key === 'Tab' || event.keyCode === 9) {
        if (!isKeyboardUser) {
          setIsKeyboardUser(true)
        }
      }
    }

    // Detect mouse usage (hide focus indicators)
    const handleMouseDown = () => {
      if (isKeyboardUser) {
        setIsKeyboardUser(false)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    window.addEventListener('mousedown', handleMouseDown)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      window.removeEventListener('mousedown', handleMouseDown)
    }
  }, [isKeyboardUser])

  const navCss = classNames(
    'flex flex-nowrap z-[100] bg-white items-center justify-between w-full h-[55px] lg:h-[71px] px-4 lg:px-11 font-effra max-w-[1128px] xl:px-0 m-auto'
  )

  const logoLinkCss = classNames(
    'inline-flex items-center focus:outline-none transition-all',
    {
      'focus:ring-2 focus:ring-blue-75 focus:ring-offset-2 rounded':
        isKeyboardUser
    }
  )

  const loginCss = classNames(
    'leading-none rounded-md border-2 border-blue-75 hover:border-blue-100 font-semibold text-blue-75 font-sansPro py-3 px-6 transition-all focus:outline-none',
    {
      'focus:ring-2 focus:ring-blue-75 focus:ring-offset-2': isKeyboardUser
    }
  )

  // Prepare navigation context for NavRegionSwitcher
  const navContext: any = {
    heapClasses: {
      login: `${siteRegion}_topnav_login`,
      mainNav:
        siteRegion === 'us' ? `topnav_entire_v` : `${siteRegion}_topnav_entire`,
      regionSwitcher: `${siteRegion}_topnav_region`,
      requestDemo: `${siteRegion}_topnav_demo`,
      restaurantSearch: `${siteRegion}_topnav_restaurants`
    },
    localizedEntries,
    navigationData: null, // Simple nav doesn't have menu data
    salesPhoneNumber,
    showLogin: true,
    showRegionSelector: showRegionSwitcher,
    showRequestDemo: false,
    showRestaurantSearch: false,
    showSalesInDesktopNav: true,
    showSalesInMobileMenu: false,
    showSalesInMobileNav: true,
    siteId,
    siteRegion
  }

  return (
    <nav
      aria-label='Toast Main Navigation'
      className='relative z-[100] border-b border-color-gray-25'
      role='navigation'
    >
      <div className={navCss}>
        <a
          aria-label='Toast Homepage'
          className={logoLinkCss}
          href={localizeLink(
            '/',
            localizationContext.language,
            localizationContext
          )}
          tabIndex={0}
        >
          <img
            alt='Toast Logo'
            className='inline-block w-[74px] lg:w-[107px] mr-3 lg:-mt-1'
            src={resolvePrefix('/svg/img-toast-logo.svg')}
          />
        </a>
        <div className='flex flex-nowrap items-center ml-auto'>
          <NavContextProvider value={navContext}>
            <SalesCta />
            <NavRegionSwitcher />
          </NavContextProvider>
          <a className={loginCss} href={loginUrl} tabIndex={0}>
            {t('Login', { defaultValue: 'Log In' })}
          </a>
        </div>
      </div>
    </nav>
  )
}

export default SimpleStaticNav
